import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useCreateChartOfAccount, useUpdateChartOfAccount, useChartOfAccounts } from '../hooks/useChartOfAccounts';
import { ChartOfAccount, ChartOfAccountFormData, AccountType } from '../types/chart-of-accounts.types';

const validationSchema = yup.object({
  accountCode: yup
    .string()
    .required('Account code is required')
    .min(2, 'Account code must be at least 2 characters'),
  name: yup
    .string()
    .required('Account name is required')
    .min(2, 'Account name must be at least 2 characters'),
  type: yup
    .string()
    .oneOf([AccountType.CREDIT, AccountType.DEBIT], 'Invalid account type')
    .required('Account type is required'),
  parentId: yup.string().nullable().optional(),
  journalEntry: yup.boolean().required(),
  isActive: yup.boolean().required(),
});

interface ChartOfAccountsFormProps {
  open: boolean;
  onClose: () => void;
  account?: ChartOfAccount | null;
  mode: 'create' | 'edit';
}

export const ChartOfAccountsForm: React.FC<ChartOfAccountsFormProps> = ({
  open,
  onClose,
  account,
  mode,
}) => {
  const createAccountMutation = useCreateChartOfAccount();
  const updateAccountMutation = useUpdateChartOfAccount();
  
  // Fetch all accounts for parent selection (excluding current account if editing)
  const { chartOfAccounts: allAccounts } = useChartOfAccounts({
    take: 1000, // Get all accounts for parent selection
  });

  type FormData = {
    accountCode: string;
    name: string;
    type: AccountType;
    parentId?: string;
    journalEntry: boolean;
    isActive: boolean;
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: {
      accountCode: '',
      name: '',
      type: AccountType.DEBIT,
      parentId: undefined,
      journalEntry: true,
      isActive: true,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (account && mode === 'edit') {
      reset({
        accountCode: account.accountCode,
        name: account.name,
        type: account.type,
        parentId: account.parentId,
        journalEntry: account.journalEntry,
        isActive: account.isActive,
      });
    } else if (mode === 'create') {
      reset({
        accountCode: '',
        name: '',
        type: AccountType.DEBIT,
        parentId: undefined,
        journalEntry: true,
        isActive: true,
      });
    }
  }, [account, mode, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      if (mode === 'create') {
        await createAccountMutation.mutateAsync(data);
      } else if (mode === 'edit' && account) {
        await updateAccountMutation.mutateAsync({
          id: account.id,
          data: { ...data, updatedBy: 'current-user' }, // Replace with actual user ID
        });
      }
      handleClose();
    } catch (error) {
      console.error('Error saving account:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const isLoading = createAccountMutation.isPending || updateAccountMutation.isPending;
  const error = createAccountMutation.error || updateAccountMutation.error;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Add New Account' : 'Edit Account'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {error && (
              <Alert severity="error">
                {error.message || 'An error occurred while saving the account'}
              </Alert>
            )}

            <Controller
              name="accountCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Account Code"
                  error={!!errors.accountCode}
                  helperText={errors.accountCode?.message}
                  fullWidth
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Account Name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.type}>
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    {...field}
                    label="Account Type"
                    disabled={isLoading}
                  >
                    <MenuItem value={AccountType.CREDIT}>Credit</MenuItem>
                    <MenuItem value={AccountType.DEBIT}>Debit</MenuItem>
                  </Select>
                  {errors.type && (
                    <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 1.75 }}>
                      {errors.type.message}
                    </Box>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="parentId"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Autocomplete
                  value={allAccounts?.find(acc => acc.id === value) || null}
                  onChange={(_, newValue) => onChange(newValue?.id || undefined)}
                  options={allAccounts?.filter(acc => acc.id !== account?.id) || []}
                  getOptionLabel={(option) => `${option.accountCode} - ${option.name}`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Parent Account (Optional)"
                      error={!!errors.parentId}
                      helperText={errors.parentId?.message}
                      disabled={isLoading}
                    />
                  )}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="journalEntry"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value}
                      onChange={onChange}
                      disabled={isLoading}
                    />
                  }
                  label="Allow Journal Entry"
                />
              )}
            />

            <Controller
              name="isActive"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value}
                      onChange={onChange}
                      disabled={isLoading}
                    />
                  }
                  label="Active"
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {mode === 'create' ? 'Create Account' : 'Update Account'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
